import type { QuestionType } from "@/api/survey-library.service";

type QuestionTypeConfig = {
  label: string;
  formLabel: string;
  hasOptions: boolean;
  rangeMax: number | null;
};

export const SURVEY_QUESTION_TYPE_CONFIG: Record<QuestionType, QuestionTypeConfig> = {
  text: {
    label: "Text",
    formLabel: "Text",
    hasOptions: false,
    rangeMax: null,
  },
  choice: {
    label: "Choice",
    formLabel: "Choice",
    hasOptions: true,
    rangeMax: null,
  },
  dropdown: {
    label: "Dropdown",
    formLabel: "Dropdown",
    hasOptions: true,
    rangeMax: null,
  },
  "range-10": {
    label: "Range 10",
    formLabel: "Range - 10 (0 to 10)",
    hasOptions: false,
    rangeMax: 10,
  },
  "range-100": {
    label: "Range 100",
    formLabel: "Range - 100 (0 to 100)",
    hasOptions: false,
    rangeMax: 100,
  },
  boolean: {
    label: "Yes / No",
    formLabel: "Yes / No",
    hasOptions: false,
    rangeMax: null,
  },
  testimonial: {
    label: "Testimonial",
    formLabel: "Testimonial",
    hasOptions: false,
    rangeMax: null,
  },
  "multi-testimonial": {
    label: "Multi Testimonial",
    formLabel: "Multi Testimonial",
    hasOptions: false,
    rangeMax: null,
  },
};

export const SURVEY_QUESTION_TYPE_OPTIONS = (
  Object.entries(SURVEY_QUESTION_TYPE_CONFIG) as [QuestionType, QuestionTypeConfig][]
).map(([value, config]) => ({
  value,
  label: config.formLabel,
}));

// Testimonial-type questions do not filter by referee context — display rules
// are meaningless for them. Single source of truth for any UI gates / persistence
// decisions that depend on this predicate.
export function isTestimonialType(type: QuestionType): boolean {
  return type === "testimonial" || type === "multi-testimonial";
}

// Only `multi-testimonial` carries a per-locale `subjectLabel`. Single source of
// truth for any UI gate that depends on the subject-label editor being shown.
export function isMultiTestimonialType(type: QuestionType): boolean {
  return type === "multi-testimonial";
}

export function supportsNoExperienceLabel(type: QuestionType): boolean {
  return type === "range-100";
}

export const MIN_CHOICE_OPTIONS = 2;

/** Returns a user-facing error when a choice question has too few options. */
export function getChoiceOptionsValidationError(
  type: QuestionType,
  optionCount: number,
): string | null {
  if (!SURVEY_QUESTION_TYPE_CONFIG[type].hasOptions) {
    return null;
  }
  if (optionCount < MIN_CHOICE_OPTIONS) {
    return `Add at least ${MIN_CHOICE_OPTIONS} answer options.`;
  }
  return null;
}
